// import * as intl from 'react-intl-universal';
import * as intl from 'react-intl-universal';
import { toParams, toQuery } from '../../utils/filter';
// moment
import 'moment/locale/en-gb';
import 'moment/locale/id';
import 'moment/locale/ja';
import 'moment/locale/ko';
import 'moment/locale/th';
import 'moment/locale/vi';
import 'moment/locale/zh-cn';
import 'moment/locale/zh-tw';
// antd
import enUS from 'antd/lib/locale-provider/en_US';
import esES from 'antd/lib/locale-provider/es_ES';
import inID from 'antd/lib/locale-provider/id_ID';
import jaJP from 'antd/lib/locale-provider/ja_JP';
import koKR from 'antd/lib/locale-provider/ko_KR';
import ptPT from 'antd/lib/locale-provider/pt_PT';
import thTH from 'antd/lib/locale-provider/th_TH';
import viVN from 'antd/lib/locale-provider/vi_VN';
import zhCN from 'antd/lib/locale-provider/zh_CN';
import zhTW from 'antd/lib/locale-provider/zh_TW';

export type Language =
    | 'en-US'
    | 'zh-CN'
    | 'zh-TW'
    | 'ko-KR'
    | 'ja-JP'
    | 'vi-VN'
    | 'th-TH'
    | 'in-ID'
    | 'ar-AE'
    | 'es-ES'
    | 'pt-PT';

// 没有定义的将默认英文
export const LangLocale: Record<string, any> = {
    'zh-CN': zhCN,
    'zh-TW': zhTW,
    'en-US': enUS,
    'ko-KR': koKR,
    'ja-JP': jaJP,
    'vi-VN': viVN,
    'th-TH': thTH,
    'in-ID': inID,
    'es-ES': esES,
    'pt-PT': ptPT,
};

/**
 * 控制所有涉及语种相关逻辑
 */
class Languages {
    // 默认语言
    private static readonly lang = 'en-US';

    // 国际语种代码
    private static code: Record<string, string> = {};

    // 支持语言
    private static supported: any[] = [];

    private static ipConfig: Record<string, string> = {};

    // 展示语言
    private static display: any[] = [];

    /**
     * 设置cookie lang
     * @param key
     * @param value
     */
    checkCookie = (key: string, value: string) => {
        document.cookie = key + "=" + value + ";path=/";
    }

    /**
     * ip 失败
     */
    ip = (country: string, region: string) => Languages.ipConfig[country] || Languages.ipConfig[region];

    /**
     * 获取本地存储默认语言
     */
    get = (): any => window.localStorage && window.localStorage.getItem('_uLang');

    /**
     * 设置当前语言
     * @param language
     * @param reload
     */
    set = async (language: Language, reload: boolean = true) => {
        if (this.get() === language) return;
        window.localStorage && window.localStorage.setItem('_uLang', language);
        this.checkCookie('lang', language);
        reload && this.reload();
    };

    /**
     * 是否支持该语种
     * @param code
     */
    isSupported = (code: string) => {
        const langs = Languages.supported.map(({ key }: { key: string }) => key);
        if (langs.includes(code)) return code;
        return Languages.code[code];
    };

    /**
     * 获取支持语言
     */
    getSupported = () =>
        Languages.supported.map((item: any) => ({
            ...item,
            name: intl.get(`Locales.${item.langKey}`).d(item.name),
        }));

    /**
     * 获取展示语言
     */
    getDisplay = () => Languages.display;

    /**
     * 语种代码对应描述
     */
    getDesc = (key = 'key') =>
        Languages.supported.reduce(
            (s: Record<string, any>, item: any) => ({
                ...s,
                [item[key]]: intl.get(`Locales.${item.langKey}`).d(item.name),
            }),
            null,
        );

    /**
     * 获取默认语种
     */
    getInitial = (): Language => Languages.lang;

    /**
     * 获取crm默认语言
     */
    getDefault = (): Language => this.get() || this.getInitial();

    /**
     * 获取url语种参数
     */
    getQuery = () => {
        const { hl } = toParams(window.location.search);
        return hl && Languages.code[hl];
    };

    /**
     * 配置组件内time的国际化语言
     */
    getLocale = () => {
        const lang = this.getDefault();
        return this.getSupported().filter(({ key }: any) => key == lang)[0].locale;
    };

    /**
     * 重新加载
     */
    reload = () => {
        let [url, query] = window.location.href.split('?');
        // 去除语种参数
        if (query && query.indexOf('hl=') > -1) {
            query = toQuery(toParams(window.location.search), ['hl']);
            url = query ? `${url}?${query}` : url;
        }

        if (window.location.hash) {
            url = url.includes(window.location.hash) ? url : `${url}${window.location.hash}`;
        }

        // 如果url中存在hash #xxx，且 location.href === url，location.href = url 不会重新加载页面，需要使用 location.reload 重新加载页面
        if (window.location.href !== url) {
            window.location.href = url;
        } else {
            window.location.reload();
        }
    };

    // 初始化
    init = ({ supported, display, ipConfigMapValue, langCode }: any) => {
        Languages.supported = supported;
        Languages.display = display.map((item: any) => ({ ...item, name: intl.get(`Locales.${item.langKey}`).d(item.name) }));
        Languages.code = langCode;
        Languages.ipConfig = ipConfigMapValue;
    };
}

export default new Languages();
